import { useCallback, useMemo, useState } from 'react';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type { WithResourceId } from '../../../types/knowledge';
import {
  allSelectedSupport,
  canResumeAbandonedDocument,
  canRetryFailedUploadDocument,
} from '../documentActionEligibility';

type BulkConfirmState =
  | { type: 'delete'; documents: Array<WithResourceId<KnowledgeBaseDocument>> }
  | { type: 'retry'; documents: Array<WithResourceId<KnowledgeBaseDocument>> }
  | null;

type UseDocumentSelectionOptions = {
  documents: Array<WithResourceId<KnowledgeBaseDocument>>;
  canManageDocuments: boolean;
  selectionScopeKey: string;
};

export type { BulkConfirmState };

export function useDocumentSelection({
  documents,
  canManageDocuments,
  selectionScopeKey,
}: UseDocumentSelectionOptions) {
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(
    () => new Set<string>(),
  );
  const [bulkConfirm, setBulkConfirm] = useState<BulkConfirmState>(null);
  const [selectionScope, setSelectionScope] = useState(selectionScopeKey);

  if (selectionScope !== selectionScopeKey) {
    setSelectionScope(selectionScopeKey);
    setSelectedDocumentIds(new Set());
    setBulkConfirm(null);
  }

  const selectedDocuments = useMemo(
    () => documents.filter((document) => selectedDocumentIds.has(document.id)),
    [documents, selectedDocumentIds],
  );
  const hasSelection = selectedDocuments.length > 0;
  const allVisibleSelected =
    documents.length > 0 &&
    documents.every((document) => selectedDocumentIds.has(document.id));
  const someVisibleSelected =
    hasSelection && !allVisibleSelected && documents.length > 0;

  const canBulkDownload = hasSelection;
  const canBulkDelete = hasSelection && canManageDocuments;
  const canBulkResume =
    canManageDocuments &&
    allSelectedSupport(selectedDocuments, canResumeAbandonedDocument);
  const canBulkRetry =
    canManageDocuments &&
    allSelectedSupport(selectedDocuments, canRetryFailedUploadDocument);

  const toggleDocumentSelected = useCallback((documentId: string) => {
    setSelectedDocumentIds((current) => {
      const next = new Set(current);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedDocumentIds((current) => {
      if (
        documents.length > 0 &&
        documents.every((document) => current.has(document.id))
      ) {
        return new Set();
      }
      return new Set(documents.map((document) => document.id));
    });
  }, [documents]);

  const clearSelection = useCallback(() => {
    setSelectedDocumentIds(new Set());
  }, []);

  return {
    selectedDocumentIds,
    selectedDocuments,
    hasSelection,
    allVisibleSelected,
    someVisibleSelected,
    canBulkDownload,
    canBulkDelete,
    canBulkResume,
    canBulkRetry,
    toggleDocumentSelected,
    toggleSelectAllVisible,
    clearSelection,
    bulkConfirm,
    setBulkConfirm,
  };
}
